import { Inject, Injectable } from '@angular/core';
import { ThemeService } from '../../services/theme/theme.service';
import { SettingsQuery } from './settings.query';
import { take, tap } from 'rxjs/operators';
import { SettingsState, SettingsStore } from './settings.store';
import { ViewStates } from '../../enums/day-info-view-states.enum';
import { LanguageBackendService } from '../../services/language/language-backend.service';
import { SessionQuery } from '../session/session.query';
import { ToastService } from '../../services/toast-service';
import { PasswordBackendService } from '../../services/password/password-backend.service';
import { SessionService } from '../session/session.service';
import { SettingsBackendService } from '../../services/settings/settings-backend.service';
import { resetStores } from '@datorama/akita';
import { AppConfig } from '../../services/config.service';
import { LangaugeService } from '../../services/language.service';
import {
  ChronoVersionEnum,
  LanguagePersonModel,
  PasswordPersonModel,
  WebAppSettingsModel,
} from '../../models/client-models';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  constructor(
    private theme: ThemeService,
    private settingsBackendService: SettingsBackendService,
    private settingsQuery: SettingsQuery,
    private languageBackendService: LanguageBackendService,
    private passwordBackendService: PasswordBackendService,
    @Inject('persistStorage') private persistStorage,
    private sessionQuery: SessionQuery,
    private settingsStore: SettingsStore,
    private toastService: ToastService,
    private sessionService: SessionService,
    private languageService: LangaugeService
  ) {}

  /**
   * Set theme to dark mode or not.
   *
   * @param darkMode true = darkMode, false=normal
   */
  setThemeMode(darkMode: boolean) {
    this.theme.changeThemeMode(darkMode);
    this.settingsStore.update((state) => ({
      ...state,
      darkMode,
    }));
  }

  setTheme(theme: string) {
    this.theme.changeTheme(theme);
    this.settingsStore.update((state) => ({
      ...state,
      theme,
    }));
  }

  setThemeFromStore() {
    this.settingsQuery
      .selectTheme()
      .subscribe((theme) => this.theme.changeTheme(theme));
  }

  setThemeModeFromStore() {
    this.settingsQuery
      .selectDarkMode()
      .subscribe((dark) => this.theme.changeThemeMode(dark));
  }

  /**
   * Set preferred view state in dayinfo view.
   *
   * @param viewState new preferred view state.
   */
  setDayInfoViewState(viewState: ViewStates): void {
    this.settingsStore.update((state) => ({
      ...state,
      preferredDayInfoViewState: viewState,
    }));
  }

  /**
   * Set preferred view state in expense view.
   *
   * @param viewState new preferred view state.
   */
  setExpenseViewState(viewState: ViewStates): void {
    this.settingsStore.update((state) => ({
      ...state,
      preferredExpenseViewState: viewState,
    }));
  }

  /**
   * Set preferred view state in booking view.
   *
   * @param viewState new preferred view state.
   */
  setBookingViewState(viewState: ViewStates): void {
    this.settingsStore.update((state) => ({
      ...state,
      preferredBookingViewState: viewState,
    }));
  }

  /**
   * Update language of person.
   *
   * @param ln Short language string (en, de, it etc.).
   */
  setLanguage(ln: string) {
    this.settingsStore.setLoading(true);

    if (this.sessionQuery.getPersonID() != 0) {
      const parameter = {
        language: ln,
        personID: this.sessionQuery.getPersonID(),
      } as LanguagePersonModel;

      this.languageBackendService
        .updateLanguage(parameter)
        .pipe(tap(() => this.settingsStore.setLoading(false)))
        .subscribe({
          next: (value) => {
            this.sessionService.setLanguage(ln);
            this.languageService.setLocale(ln);
            this.toastService.presentInfoToast({ message: value.message });
          },
          error: (error) => {
            this.toastService.presentErrorToast({
              message: error.error.message,
            });
          },
        });
    } else {
      this.settingsStore.update((state) => ({
        ...state,
        userLanguage: ln,
      }));
      this.languageService.setLocale(ln);
    }
  }

  /**
   * Save new password.
   *
   * @param pw new password.
   */
  savePassword(pw: string) {
    this.settingsStore.setLoading(true);
    const parameter = {
      password: pw,
      personID: this.sessionQuery.getPersonID(),
    } as PasswordPersonModel;

    this.passwordBackendService
      .updatePassword(parameter)
      .pipe(tap(() => this.settingsStore.setLoading(false)))
      .subscribe({
        next: (value) => {
          this.toastService.presentInfoToast({ message: value.message });
        },
        error: (error) => {
          this.toastService.presentErrorToast({ message: error.error.message });
        },
      });
  }

  setShowOperation(value: boolean) {
    this.settingsStore.update((s) => ({ ...s, showOperation: !!value }));
  }
  setShowMultiOrder(value: boolean) {
    this.settingsStore.update((s) => ({ ...s, showMultiOrder: !!value }));
  }
  setShowBarcodeScanner(value: boolean) {
    this.settingsStore.update((s) => ({ ...s, showBarcodeScanner: !!value }));
  }
  setShowTimeInEdit(value: boolean) {
    this.settingsStore.update((s) => ({ ...s, showTimeInEdit: !!value }));
  }

  // resetAll() {
  //   const idPerson = this.sessionQuery.getPersonID();

  //   this.settingsBackendService
  //     .setSettings({
  //       idPerson,
  //       value: '',
  //       version: AppConfig.settings.version,
  //       minorVersion: AppConfig.settings.minorVersion,
  //     } as WebAppSettingsModel)
  //     .pipe(take(1))
  //     .subscribe();

  //   resetStores();
  //   this.persistStorage.clearStore();
  // }

  setSettings(settings: SettingsState) {
    const chronoVersion = this.sessionQuery.getChronoVersion();

    if (chronoVersion === ChronoVersionEnum.Standard) {
      settings.showBarcodeScanner = false;
      settings.showMultiOrder = false;
      settings.showOperation = false;
    }

    this.settingsStore.update((state) => ({
      ...state,
      ...settings,
    }));
  }
}
